import React, { useCallback, useMemo } from 'react'
import { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { dispatchAppEvent } from '@/src/renderer/lib/events'
import { SidebarItem } from '../SidebarItem'
import { AreaIcon } from '../../../../components/icons/AreaIcon'
import { CircularProgress } from '../../../ui/CircularProgress'
import { ProjectIcon } from '../../../common/ProjectIcon'
import { projectAccentColor, titleColorOf } from '../../../../lib/projectIcon/projectTitle'
import { useProjectTaskProgress } from '../../../../features/taskManagement/hooks/useProjectTaskProgress'
import { ProjectSidebarItemProps } from './ProjectSidebarItem.types'

export const ProjectSidebarItem: React.FC<ProjectSidebarItemProps> = ({
  project,
  lifeSpace,
  activeTab,
  depth = 0,
  hasChildren = false,
  expanded,
  onToggleExpand,
  openPaths,
  previewPaths,
  pinnedPaths,
  onOpenTab,
  onDoubleClick,
  onPromote,
  onCloseTabByPath,
  onUnpinByPath,
  onPinByPath,
  openTab,
  onDelete,
  subprojectCount = 0,
  onAddSubproject,
  dimmed,
  dragging,
  dragHandle,
  favorite,
  onFavoriteChange,
  inTree = true,
}) => {
  const { t } = useTranslation()
  const path = `/home/${lifeSpace}/project/${project.id}`
  const title = project.name || t('project.defaultName')
  const labelColor = useMemo(() => titleColorOf(project.nameHtml), [project.nameHtml])
  const accentColor = useMemo(
    () => projectAccentColor({ icon: project.icon, iconColor: project.iconColor, nameHtml: project.nameHtml }),
    [project.icon, project.iconColor, project.nameHtml],
  )

  const navigate = useNavigate()
  const isProjectActive = activeTab?.path === path

  // Progress donut only for the active project AND when:
  //   - the project has at least four countable tasks (total >= 4), AND
  //   - at least one of them is completed (done > 0)
  // Tiny / untouched projects stay quiet. On hover the close-tab button grows
  // in behind the donut and pushes it left (SidebarItem, like the badge).
  const progress = useProjectTaskProgress(isProjectActive ? project.id : undefined)
  const showProgress = isProjectActive && progress.total >= 4 && progress.done > 0
  const trailing = showProgress
    ? (
      <CircularProgress
        value={progress.value}
        ariaLabel={t('project.progressLabel', {
          done: progress.done,
          total: progress.total,
          defaultValue: `${progress.done} of ${progress.total} tasks done`,
        })}
      />
    )
    : undefined

  // Ein Klick auf das schon offene Projekt scrollt nur nach oben. Auf- und zuklappen
  // geht allein über den Pfeil (#185) — vorher klappte der Klick die Abschnitte.
  const handleProjectClick = useCallback((e: React.MouseEvent) => {
    if (isProjectActive) {
      // Signal ProjectPage to suppress scroll-spy during the smooth scroll
      dispatchAppEvent('scroll-spy-suppress')
      // Clear section param and scroll to top
      navigate(path, { replace: true })
      const scrollWrap = document.querySelector('.main-layout__scroll-wrap')
      if (scrollWrap) scrollWrap.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    onOpenTab(e, `${lifeSpace}-project-${project.id}`, path, title, AreaIcon as unknown as LucideIcon)
  }, [isProjectActive, navigate, path, onOpenTab, lifeSpace, project.id, title])

  const handleOpenNewTab = useCallback(() => {
    openTab({
      id: `${lifeSpace}-project-${project.id}-${Date.now()}`,
      path,
      title,
      icon: AreaIcon as unknown as LucideIcon,
      lifeSpace,
      isDirty: false,
      isPinned: false,
    })
  }, [openTab, lifeSpace, project.id, path, title])

  return (
    <SidebarItem
      icon={AreaIcon as unknown as LucideIcon}
      label={title}
      path={path}
      isActive={isProjectActive}
      hasOpenTab={openPaths.has(path)}
      isPreview={previewPaths.has(path)}
      isPinned={pinnedPaths.has(path)}
      hasSubItems={inTree && hasChildren}
      isSubMenuExpanded={inTree && hasChildren && expanded}
      onToggleSubMenu={onToggleExpand}
      onUnpin={onUnpinByPath ? () => onUnpinByPath(path) : undefined}
      onPinTab={onPinByPath ? () => onPinByPath(path) : undefined}
      onCloseTab={onCloseTabByPath ? () => onCloseTabByPath(path) : undefined}
      onOpenNewTab={handleOpenNewTab}
      onClick={handleProjectClick}
      onDoubleClick={() => onDoubleClick(path)}
      onPromote={onPromote}
      onDelete={onDelete}
      deleteDescription={subprojectCount > 0
        ? t('project.confirmDelete.descriptionWithSubprojects', { name: title, count: subprojectCount })
        : undefined}
      onAddSubproject={onAddSubproject}
      depth={inTree ? depth : 0}
      treeLevel={inTree ? depth + 1 : undefined}
      favorite={favorite}
      onFavoriteChange={onFavoriteChange}
      dimmed={dimmed}
      dragging={dragging}
      dragHandle={dragHandle}
      trailing={trailing}
      labelColor={labelColor}
      accentColor={accentColor}
      projectIcon={{
        node: <ProjectIcon icon={project.icon} iconColor={project.iconColor} size={26} />,
        glow: project.icon
          ? <ProjectIcon icon={project.icon} iconColor={project.iconColor} size={32} />
          : undefined,
      }}
    />
  )
}
